import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import { Plus } from 'lucide-react';
import axiosInstance from '../../../redux/api';
import { Badge } from 'Components/ui/badge';
import { Button } from 'Components/ui/button';
import { Skeleton } from 'Components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'Components/ui/table';
import {
  PageBody, PageDescription, PageHeader, PageHeaderRow, PageShell, PageTitle,
} from 'Components/ui/page-shell';
import { cn } from 'lib/utils';
import Segmented from './Segmented';
import { formatDateTime } from '../Order/shared/orderFormat';
import {
  STATUSES, audienceLabel, opensLabel, rangeLabel, reachedLabel, statusView, whenLabel,
} from './pushHistoryView';

// Settings → Notification → History: every notification written on the
// Push notification page, sent or scheduled. Automatic pushes (order
// updates, cart and wishlist reminders) are not here.

const PAGE_ROWS = 20;

const serverMessage = (error, fallback) => error?.response?.data?.message || fallback;

// Arabic reads right to left: set the direction so punctuation lands on the correct side.
const textDir = (text) => (/[؀-ۿ]/.test(text || '') ? 'rtl' : 'auto');

const Muted = ({ children, className }) => (
  <p className={cn('mt-0.5 text-xs text-muted-foreground', className)}>{children}</p>
);

const LoadingRows = () => (
  <>
    {[0, 1, 2, 3, 4].map((n) => (
      <TableRow key={n}>
        {[0, 1, 2, 3, 4, 5, 6].map((c) => (
          <TableCell key={c}>
            <Skeleton className='h-4 w-full' />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const PushHistory = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('admin/customers/push-notifications', {
        params: { page, per_page: PAGE_ROWS, status: status || undefined },
      });
      const body = response?.data?.response || {};
      setRows(body.data || []);
      setPagination(body.pagination || null);
    } catch (e) {
      setError(serverMessage(e, 'The history could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    load();
  }, [load]);

  const filterBy = (option) => {
    setStatus(option.value);
    setPage(1);
  };

  const cancel = async (row) => {
    const confirmed = await swal({
      title: 'Cancel this notification?',
      text: `“${row.title}” is scheduled for ${formatDateTime(row.scheduled_for)}. It won’t be sent.`,
      icon: 'warning',
      buttons: ['Keep it', 'Cancel it'],
      dangerMode: true,
    });
    if (!confirmed) return;
    setCancelling(row.id);
    try {
      await axiosInstance.post(`admin/customers/push-notifications/${row.id}/cancel`);
    } catch (e) {
      swal('Not cancelled', serverMessage(e, 'Something went wrong while cancelling.'), 'error');
    } finally {
      setCancelling(null);
      await load();
    }
  };

  const pages = pagination?.pages || 0;
  const emptyText = status
    ? `No ${STATUSES.find((s) => s.value === status)?.label.toLowerCase()} notifications.`
    : 'Nothing sent or scheduled yet.';

  return (
    <PageShell>
      <PageHeader>
        <PageHeaderRow>
          <div>
            <PageTitle>Notification history</PageTitle>
            <PageDescription>
              Every notification written on the Push notification page, sent or scheduled. Order updates and
              reminders go out on their own and aren’t listed.
            </PageDescription>
          </div>
          {/* Dark, not bg-primary: Bootstrap's .bg-primary (!important) would paint it bright blue. */}
          <Button
            type='button'
            onClick={() => navigate('/notification')}
            className='h-10 border-0 bg-foreground px-4 text-background shadow-none hover:bg-foreground/90'
          >
            <Plus size={16} className='mr-1.5' />
            New notification
          </Button>
        </PageHeaderRow>
      </PageHeader>

      <PageBody className='pt-6'>
        <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
          <Segmented label='Status' options={STATUSES} value={status} onChange={filterBy} disabled={loading} />
          <p className='text-sm text-muted-foreground'>{loading ? '' : rangeLabel(pagination)}</p>
        </div>

        <div className='overflow-hidden rounded-xl border bg-white'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className='w-[170px]'>When</TableHead>
                <TableHead className='w-[120px]'>Status</TableHead>
                <TableHead>Notification</TableHead>
                <TableHead className='w-[180px]'>Opens</TableHead>
                <TableHead className='w-[130px]'>Who</TableHead>
                <TableHead className='w-[170px]'>By</TableHead>
                <TableHead className='w-[90px]' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <LoadingRows />
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} className='py-10 text-center text-sm text-destructive'>
                    {error}
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className='py-10 text-center text-sm text-muted-foreground'>
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const view = statusView(row.status);
                  return (
                    <TableRow key={row.id} className='align-top'>
                      <TableCell className='whitespace-nowrap font-medium text-foreground'>
                        {whenLabel(row)}
                        {row.status === 'scheduled' && <Muted>Riyadh time</Muted>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={view.variant}>{view.label}</Badge>
                        {row.status === 'failed' && row.failure && <Muted className='text-destructive'>{row.failure}</Muted>}
                        {row.status === 'cancelled' && (
                          <Muted>
                            {row.cancelled_by ? `by ${row.cancelled_by}` : ''}
                            {row.cancelled_on ? ` · ${formatDateTime(row.cancelled_on)}` : ''}
                          </Muted>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className='flex gap-3'>
                          {row.image ? (
                            <img src={row.image} alt='' className='h-10 w-10 shrink-0 rounded-lg border object-cover' />
                          ) : (
                            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#111] text-sm font-semibold text-[#c9a96e]'>
                              N
                            </div>
                          )}
                          <div className='min-w-0'>
                            <p dir={textDir(row.title)} className='truncate font-medium text-foreground'>
                              {row.title}
                            </p>
                            <p dir={textDir(row.body)} className='line-clamp-2 whitespace-pre-line text-sm text-muted-foreground'>
                              {row.body}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className='text-sm'>{opensLabel(row.opens)}</TableCell>
                      <TableCell className='text-sm'>
                        {audienceLabel(row.audience)}
                        {reachedLabel(row) && <Muted>{reachedLabel(row)}</Muted>}
                      </TableCell>
                      <TableCell className='text-sm'>
                        {row.created_by || '—'}
                        <Muted>{formatDateTime(row.created_on)}</Muted>
                      </TableCell>
                      <TableCell className='text-right'>
                        {row.status === 'scheduled' && (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            className='shadow-none'
                            disabled={cancelling === row.id}
                            onClick={() => cancel(row)}
                          >
                            {cancelling === row.id ? 'Cancelling…' : 'Cancel'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {pages > 1 && (
          <div className='mt-4 flex items-center justify-end gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='shadow-none'
              disabled={loading || page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span className='text-sm text-muted-foreground'>
              Page {page} of {pages}
            </span>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='shadow-none'
              disabled={loading || page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </PageBody>
    </PageShell>
  );
};

export default PushHistory;
